import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUTMTracking } from '../../../hooks/useUTMTracking';
import { useABTest } from '../../../hooks/useABTest';

const HERO_FORM_URL = 'https://readdy.ai/api/form/d6pjrblsbgrc47filog0';

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

// Variant content defined outside component to avoid re-creation
const variants = {
  A: {
    headline: 'Life Insurance in Indianapolis, Indiana — Protecting Your Family for Less',
    subheadline: 'Hoosier Life Insurance is Indiana\'s independent agency serving individuals, families, and seniors statewide. Affordable term life, final expense, IUL & annuities from 20+ A-rated carriers — free quote in minutes, no pressure.',
    ctaText: 'Get My Free Quote Now',
    formTitle: 'Get Your Free Quote',
  },
  B: {
    headline: "Don't Leave Your Family Unprotected — Get Covered Today",
    subheadline: 'Hoosier Life Insurance serves Indiana individuals, families, and seniors across all 92 counties. Fast, affordable quotes comparing 20+ top-rated carriers — term life, whole life, final expense, and annuities. Zero pressure, real protection.',
    ctaText: 'See My Rates in 60 Seconds',
    formTitle: 'Get Your Rate — Free & Fast',
  },
};

export default function ABTestHero() {
  // isReady is intentionally unused for rendering — we show A immediately
  const { variant } = useABTest({ testId: 'hero_headline', variants: ['A', 'B'] });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    zip: '',
    insurance_type: '',
    best_time: '',
  });
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [smsConsent, setSmsConsent] = useState(false);
  const navigate = useNavigate();
  const { utmParams } = useUTMTracking();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([k, v]) => body.append(k, v));
      body.append('sms_consent', smsConsent.toString());
      body.append('ab_variant', variant);
      if (utmParams.utm_source) body.append('utm_source', utmParams.utm_source);
      if (utmParams.utm_medium) body.append('utm_medium', utmParams.utm_medium);
      if (utmParams.utm_campaign) body.append('utm_campaign', utmParams.utm_campaign);
      if (utmParams.traffic_source) body.append('traffic_source', utmParams.traffic_source);
      if (utmParams.referrer) body.append('referrer', utmParams.referrer);

      const res = await fetch(HERO_FORM_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setStatus(res.ok ? 'success' : 'error');
      if (res.ok) {
        setFormData({ name: '', email: '', phone: '', zip: '', insurance_type: '', best_time: '' });
        setSmsConsent(false);
      }
    } catch {
      setStatus('error');
    }
  };

  // Always render immediately — use variant A as default, swap to B once resolved
  const content = variant === 'B' ? variants.B : variants.A;

  return (
    <section id="home" aria-label="Hero — Get Your Free Life Insurance Quote" className="pt-[90px] md:pt-[120px] pb-12 md:pb-[100px] bg-gradient-to-br from-[#0F2A3F] via-[#1A3A52] to-[#0D2235] relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#D97642]/10 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/4"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#D97642]/8 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4"></div>
      </div>

      {/* A/B variant badge — admin/dev only, hidden from real visitors via CSS */}
      <div className="fixed bottom-6 left-6 z-50 bg-black/80 backdrop-blur-sm text-white px-4 py-2 rounded-lg text-[11px] font-mono border border-white/20 shadow-lg hidden" aria-hidden="true">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></div>
          <span>A/B Test: Variant <strong className="text-emerald-400">{variant}</strong></span>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-16 items-center">

          {/* Left — Headline + Image */}
          <div className="flex flex-col gap-6">
            <div>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-[#D97642]/20 text-[#F0A070] text-[12px] font-semibold uppercase tracking-widest rounded-full mb-4 border border-[#D97642]/30">
                <i className="ri-shield-check-fill text-[13px]" aria-hidden="true"></i>
                Serving Central Indiana Families
              </span>

              {variant === 'B' && (
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 text-amber-300 text-[12px] font-bold uppercase tracking-wide rounded-full mb-4 ml-2 border border-amber-500/40 animate-pulse">
                  <i className="ri-flashlight-fill text-[13px]" aria-hidden="true"></i>
                  Limited Spots This Week
                </div>
              )}

              <h1 className="text-[34px] sm:text-[44px] md:text-[52px] font-bold text-white leading-[1.15] mb-5">
                {content.headline}
              </h1>
              <p className="text-[16px] sm:text-[18px] text-white/75 leading-[1.7]">
                {content.subheadline}
              </p>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap gap-3">
              {[
                { icon: 'ri-time-line', label: 'Response in 24 hrs' },
                { icon: 'ri-lock-line', label: '100% Private & Secure' },
                { icon: 'ri-map-pin-line', label: 'Local Indianapolis Agent' },
              ].map((b) => (
                <div key={b.label} className="flex items-center gap-2 px-3 py-2 bg-white/10 rounded-lg border border-white/20 backdrop-blur-sm">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${b.icon} text-[15px] text-[#D97642]`} aria-hidden="true"></i>
                  </div>
                  <span className="text-[13px] text-white/90 font-medium">{b.label}</span>
                </div>
              ))}
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/15 rounded-lg border border-emerald-500/30 backdrop-blur-sm">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-video-chat-line text-[15px] text-emerald-400" aria-hidden="true"></i>
                </div>
                <span className="text-[13px] text-emerald-300 font-semibold">Video Consultations Until 9 PM</span>
              </div>
            </div>

            {/* Hero image — desktop only, explicit dimensions to prevent layout shift */}
            <div className="relative hidden lg:block w-full h-[320px]">
              <div className="absolute inset-0 bg-gradient-to-br from-[#D97642]/20 to-transparent rounded-[24px]"></div>
              <img
                src="https://public.readdy.ai/ai/img_res/15e1e35ae3b8c855046feb1dffc31cec.jpg"
                alt="Indianapolis Life Insurance Agent with Local Indiana Family"
                title="Trusted Life Insurance Agent in Indianapolis, Indiana — Hoosier Life Insurance"
                loading="eager"
                fetchPriority="high"
                decoding="async"
                width={800}
                height={500}
                className="w-full h-[320px] object-cover object-top rounded-[24px] shadow-[0_12px_40px_rgba(0,0,0,0.30)] relative z-10"
              />
            </div>

            {/* Book call CTA */}
            <div>
              <button
                onClick={() => navigate('/wealth-strategy-call')}
                className="inline-flex items-center gap-3 px-7 py-4 bg-[#D97642] text-white text-[15px] font-semibold rounded-xl hover:bg-[#C26638] transition-all hover:shadow-xl shadow-md cursor-pointer whitespace-nowrap group"
              >
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className="ri-calendar-check-fill text-[20px] text-white" aria-hidden="true"></i>
                </div>
                Book a Free Wealth Strategy Call
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-arrow-right-line text-[18px] group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </div>
              </button>
              <p className="mt-2.5 text-[13px] text-white/50">
                <i className="ri-time-line mr-1" aria-hidden="true"></i>15 minutes · No obligation · 100% free
              </p>
            </div>
          </div>

          {/* Right — Lead Capture Form */}
          <div className="bg-white rounded-[24px] shadow-[0_8px_48px_rgba(0,0,0,0.25)] p-7 md:p-9 border border-white/10">
            {status === 'success' ? (
              <div role="status" aria-live="polite" className="flex flex-col items-center justify-center py-10 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mb-5">
                  <i className="ri-check-double-line text-[32px] text-green-600" aria-hidden="true"></i>
                </div>
                <h3 className="text-[22px] font-bold text-[#1A3A52] mb-3">You're All Set!</h3>
                <p className="text-[15px] text-[#4A5568] leading-[1.7] mb-6 max-w-[320px]">
                  Thanks for reaching out! We'll review your info and contact you within 24 hours with personalized options.
                </p>
                <div className="w-full bg-gradient-to-r from-[#D97642] to-[#C26638] rounded-xl p-5 mb-6">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-9 h-9 flex items-center justify-center bg-white/20 rounded-full shrink-0">
                      <i className="ri-vip-crown-fill text-[20px] text-white" aria-hidden="true"></i>
                    </div>
                    <div className="text-left">
                      <div className="text-[15px] font-bold text-white">Priority Lead — You're First in Line</div>
                      <div className="text-[12px] text-white/80">Expect a call within 24 hours</div>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => setStatus('idle')}
                  className="px-6 py-2.5 bg-[#1A3A52] text-white text-[14px] font-medium rounded-lg hover:bg-[#14304A] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Submit Another Request
                </button>
              </div>
            ) : (
              <>
                <div className="mb-6">
                  <h2 className="text-[22px] font-bold text-[#1A3A52] mb-1">{content.formTitle}</h2>
                  <p className="text-[13px] text-[#4A5568]">Fill in your details — we'll do the rest. No spam, ever.</p>
                </div>

                <form
                  id="hero-lead-capture-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="space-y-4"
                >
                  <input type="hidden" name="ab_variant" value={variant} />
                  <input type="hidden" name="utm_source" value={utmParams.utm_source || ''} />
                  <input type="hidden" name="utm_medium" value={utmParams.utm_medium || ''} />
                  <input type="hidden" name="utm_campaign" value={utmParams.utm_campaign || ''} />
                  <input type="hidden" name="traffic_source" value={utmParams.traffic_source || ''} />
                  <input type="hidden" name="referrer" value={utmParams.referrer || ''} />

                  {/* Full Name */}
                  <div>
                    <label htmlFor="hero_name" className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                      Full Name <span className="text-[#D97642]" aria-hidden="true">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none" aria-hidden="true">
                        <i className="ri-user-line text-[16px] text-gray-400"></i>
                      </div>
                      <input
                        id="hero_name"
                        type="text"
                        name="name"
                        required
                        placeholder="Jane Smith"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="hero_email" className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                      Email Address <span className="text-[#D97642]" aria-hidden="true">*</span>
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none" aria-hidden="true">
                        <i className="ri-mail-line text-[16px] text-gray-400"></i>
                      </div>
                      <input
                        id="hero_email"
                        type="email"
                        name="email"
                        required
                        placeholder="jane@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                      />
                    </div>
                  </div>

                  {/* Phone + ZIP */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label htmlFor="hero_phone" className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                        Phone <span className="text-[#D97642]" aria-hidden="true">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none" aria-hidden="true">
                          <i className="ri-phone-line text-[16px] text-gray-400"></i>
                        </div>
                        <input
                          id="hero_phone"
                          type="tel"
                          name="phone"
                          required
                          placeholder="(317) 000-0000"
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label htmlFor="hero_zip" className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                        ZIP Code <span className="text-[#D97642]" aria-hidden="true">*</span>
                      </label>
                      <div className="relative">
                        <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none" aria-hidden="true">
                          <i className="ri-map-pin-line text-[16px] text-gray-400"></i>
                        </div>
                        <input
                          id="hero_zip"
                          type="text"
                          name="zip"
                          required
                          placeholder="46201"
                          pattern="[0-9]{5}"
                          maxLength={5}
                          value={formData.zip}
                          onChange={handleChange}
                          className="w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#1A3A52] placeholder-gray-400 focus:outline-none focus:border-[#D97642] transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Insurance Type */}
                  <div>
                    <label htmlFor="insurance_type" className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                      What Are You Looking For?
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none" aria-hidden="true">
                        <i className="ri-shield-line text-[16px] text-gray-400"></i>
                      </div>
                      <select
                        id="insurance_type"
                        name="insurance_type"
                        value={formData.insurance_type}
                        onChange={handleChange}
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-[#1A3A52] focus:outline-none focus:border-[#D97642] transition-colors cursor-pointer bg-white appearance-none"
                      >
                        <option value="">Not sure — help me decide</option>
                        <option value="term_life">Term Life Insurance</option>
                        <option value="whole_life">Whole Life Insurance</option>
                        <option value="final_expense">Final Expense Insurance</option>
                        <option value="mortgage_protection">Mortgage Protection</option>
                        <option value="annuity">Annuity / Retirement Planning</option>
                        <option value="financial_analysis">Free Financial Needs Analysis</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none" aria-hidden="true">
                        <i className="ri-arrow-down-s-line text-[16px] text-gray-400"></i>
                      </div>
                    </div>
                  </div>

                  {/* Best Time to Call */}
                  <div>
                    <label htmlFor="best_time" className="block text-[13px] font-semibold text-[#1A3A52] mb-1.5">
                      Best Time to Call
                    </label>
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center pointer-events-none" aria-hidden="true">
                        <i className="ri-time-line text-[16px] text-gray-400"></i>
                      </div>
                      <select
                        id="best_time"
                        name="best_time"
                        value={formData.best_time}
                        onChange={handleChange}
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-[#1A3A52] focus:outline-none focus:border-[#D97642] transition-colors cursor-pointer bg-white appearance-none"
                      >
                        <option value="">Any time</option>
                        <option value="morning">Morning (8am – 12pm)</option>
                        <option value="afternoon">Afternoon (12pm – 5pm)</option>
                        <option value="evening">Evening (5pm – 8pm)</option>
                      </select>
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none" aria-hidden="true">
                        <i className="ri-arrow-down-s-line text-[16px] text-gray-400"></i>
                      </div>
                    </div>
                  </div>

                  {/* SMS Consent */}
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <label className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        name="sms_consent"
                        checked={smsConsent}
                        onChange={(e) => setSmsConsent(e.target.checked)}
                        required
                        className="mt-0.5 w-4 h-4 accent-[#D97642] shrink-0 cursor-pointer"
                      />
                      <span className="text-[12px] text-[#4A5568] leading-relaxed">
                        <strong className="text-[#1A3A52]">I agree to receive SMS/text messages</strong> from Hoosier Life Insurance at the phone number provided. Message frequency varies. Message &amp; data rates may apply. Reply <strong>STOP</strong> to opt out at any time. Reply <strong>HELP</strong> for help. Consent is not a condition of purchase. See our{' '}
                        <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-[#D97642] underline hover:text-[#C26638]">Privacy Policy</a> for full details.
                      </span>
                    </label>
                  </div>

                  {status === 'error' && (
                    <p role="alert" className="text-[13px] text-red-600 flex items-center gap-1.5">
                      <i className="ri-error-warning-line text-[15px]" aria-hidden="true"></i>
                      Something went wrong. Please try again or call (317) 648-2983.
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'loading' || !smsConsent}
                    aria-label={status === 'loading' ? 'Submitting your quote request' : content.ctaText}
                    className="w-full py-3.5 bg-[#D97642] text-white text-[15px] font-bold rounded-xl hover:bg-[#C26638] transition-all hover:shadow-lg cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-2"
                  >
                    {status === 'loading' ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-[18px]" aria-hidden="true"></i>
                        Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-fill text-[16px]" aria-hidden="true"></i>
                        {content.ctaText}
                      </>
                    )}
                  </button>

                  <p className="text-[11px] text-[#718096] text-center leading-[1.6]">
                    By submitting, you agree to be contacted by Hoosier Life Insurance. We never sell your information.
                  </p>
                </form>
              </>
            )}
          </div>

        </div>
      </div>
    </section>
  );
}
